import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
});

// Sesión vencida: en vez de recargar la página (y perder lo que se estaba cargando), los pedidos quedan
// en espera hasta que el usuario reingresa su contraseña en el aviso; luego se reintentan solos.
let esperaSesion = null;
let finSesion = null;
const esLoginUrl = (url = '') => /\/(auth|cliente-auth)\/login|\/health/.test(url);
const enPantallaLogin = () => /^\/(portal\/)?login/.test(window.location.pathname);
export const sesionVencida = () => !!esperaSesion;
export const abrirSesionVencida = () => {
  if (!esperaSesion) {
    esperaSesion = new Promise((resolve, reject) => { finSesion = { resolve, reject }; });
    esperaSesion.catch(() => {});
    window.dispatchEvent(new Event('freshen:sesion-vencida'));
  }
  return esperaSesion;
};
export const cerrarSesionVencida = (renovada) => {
  if (!esperaSesion) return;
  if (renovada) finSesion.resolve(); else finSesion.reject(new Error('sesion-cerrada'));
  esperaSesion = null;
  finSesion = null;
};

// Si el usuario elige salir, los pedidos en espera se descartan sin disparar errores en pantalla
const esperarSesion = () => esperaSesion.catch(() => new Promise(() => {}));
const getsEnEspera = new Map();

api.interceptors.request.use(async (config) => {
  if (esperaSesion && !config._sinEspera && !esLoginUrl(config.url)) {
    if ((config.method || 'get').toLowerCase() === 'get') {
      // Los sondeos periódicos no se acumulan: los GET idénticos en espera comparten una sola respuesta
      const clave = `${config.url}|${JSON.stringify(config.params || {})}`;
      const base = axios.getAdapter(axios.defaults.adapter);
      config.adapter = (cfg) => {
        let p = getsEnEspera.get(clave);
        if (!p) {
          p = esperarSesion().then(() => {
            getsEnEspera.delete(clave);
            cfg.headers.set('Authorization', `Bearer ${localStorage.getItem('token')}`);
            return base(cfg);
          });
          getsEnEspera.set(clave, p);
        }
        return p;
      };
      return config;
    }
    await esperarSesion();
  }
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Errores de validación del servidor (lista de {loc, msg, type}) → un texto legible: nunca un objeto en pantalla
const CAMPOS = { email: 'correo', password: 'contraseña', new_password: 'contraseña', nombre: 'nombre', telefono: 'teléfono', fecha: 'fecha' };
const textoValidacion = (d) => {
  const uno = (e) => {
    if (!e || typeof e !== 'object') return String(e ?? '');
    const campo = Array.isArray(e.loc) ? e.loc[e.loc.length - 1] : '';
    const nombre = CAMPOS[campo] || String(campo || '').replace(/_/g, ' ');
    if (/email/i.test(`${campo} ${e.msg}`) && e.type === 'value_error') return 'El correo no es válido';
    if (e.type === 'missing') return `Falta completar: ${nombre}`;
    if (e.type === 'string_too_long') return `${nombre}: el texto es demasiado largo`;
    if (e.type === 'string_too_short') return `${nombre}: el texto es demasiado corto`;
    return nombre ? `${nombre}: ${e.msg || 'valor no válido'}` : (e.msg || 'Dato no válido');
  };
  return [...new Set((Array.isArray(d) ? d : [d]).map(uno))].join(' · ');
};

export const SIN_PESTANA = 'Esta sección no está habilitada para tu usuario';
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const data = error.response?.data;
    if (data && data.detail != null && typeof data.detail === 'object') data.detail = textoValidacion(data.detail);
    const cfg = error.config || {};
    // Sesión restaurada sin servidor (token posiblemente vencido): AuthContext la refresca de fondo.
    // El 401 del propio formulario de login ("Credenciales incorrectas") tampoco abre el aviso.
    const offline = localStorage.getItem('freshen_sesion_offline') === '1';
    if (error.response?.status === 401 && !esLoginUrl(cfg.url) && !enPantallaLogin() && !offline && localStorage.getItem('token')) {
      const espera = abrirSesionVencida();
      if (cfg._sinReintento || cfg._reintentoSesion) return Promise.reject(error);
      try { await espera; } catch { return new Promise(() => {}); }
      return api({ ...cfg, _reintentoSesion: true });
    }
    // El admin cambió las pestañas de este usuario: se actualizan sus permisos en pantalla
    if (error.response?.status === 403 && data?.detail === SIN_PESTANA) window.dispatchEvent(new Event('freshen:permisos'));
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password }, { timeout: 20000 });
export const getResumenCliente = (clienteId, mes) => api.get(`/clientes/${clienteId}/resumen-mensual`, { params: { mes } });
export const getCalendariosEnviados = (fecha_desde, fecha_hasta) => api.get('/ordenes/calendario/enviados', { params: { fecha_desde, fecha_hasta } });
export const vaciarHistorialCorreos = () => api.delete('/correos');
export const enviarResumenCliente = (clienteId, payload) => api.post(`/clientes/${clienteId}/resumen-mensual/enviar`, payload);
export const getBloqueosLogin = () => api.get('/auth/bloqueos');
export const desbloquearLogin = (email) => api.post('/auth/bloqueos/desbloquear', { email });
export const refreshToken = () => api.post('/auth/refresh', null, { timeout: 20000, _sinEspera: true, _sinReintento: true });
export const health = () => api.get('/health', { timeout: 15000 });
export const getMe = () => api.get('/auth/me');
export const registerUser = (userData, config) => api.post('/auth/register', userData, config);
export const requestPasswordReset = (email) => api.post('/auth/request-password-reset', { email });
export const resetPassword = (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword });

// Users
export const getUsers = () => api.get('/users');
// Aviso / recordatorio de servicio (Programación)
export const getAvisoOrden = (ordenId) => api.get(`/ordenes/${ordenId}/aviso`);
export const enviarAvisoOrden = (ordenId, { destinatarios, asunto, mensaje }, pdfs = []) => {
  const fd = new FormData();
  destinatarios.forEach((d) => fd.append('destinatarios', d));
  fd.append('asunto', asunto || '');
  fd.append('mensaje', mensaje || '');
  pdfs.forEach((f) => fd.append('pdfs', f, f.name));
  return api.post(`/ordenes/${ordenId}/enviar-aviso`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
};
export const getPdfAviso = (ordenId, pdfId) => api.get(`/ordenes/${ordenId}/aviso/pdf/${pdfId}`, { responseType: 'blob' });
export const getAvisoMensaje = () => api.get('/settings/aviso-mensaje');
export const setAvisoMensaje = (payload) => api.put('/settings/aviso-mensaje', payload);
export const getCalendarioMensaje = () => api.get('/settings/calendario-mensaje');
export const getCorreosInternos = () => api.get('/settings/correos-internos');
export const setCorreosInternos = (correos) => api.put('/settings/correos-internos', { correos });
export const setCalendarioMensaje = (payload) => api.put('/settings/calendario-mensaje', payload);
export const getReporteMensaje = () => api.get('/settings/reporte-mensaje');
export const setReporteMensaje = (payload) => api.put('/settings/reporte-mensaje', payload);
export const getCorreoReporteServicio = (servicioId) => api.get(`/servicios/${servicioId}/correo-reporte`);
export const getPortalQr = () => api.get('/settings/portal-qr');
export const setPortalQr = (url) => api.put('/settings/portal-qr', { url });
export const getRecomendacionesTecnicas = () => api.get('/settings/recomendaciones');
export const setRecomendacionesTecnicas = (items) => api.put('/settings/recomendaciones', { items });
export const getUser = (id) => api.get(`/users/${id}`);
export const updateUser = (id, data) => api.patch(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Clientes
export const getClientes = (params = {}) => api.get('/clientes', { params });
export const getCliente = (id) => api.get(`/clientes/${id}`);
export const createCliente = (data) => api.post('/clientes', data);
export const updateCliente = (id, data) => api.patch(`/clientes/${id}`, data);
export const uploadContratoPdf = (clienteId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/clientes/${clienteId}/contrato-pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getStatsContratos = () => api.get('/stats/contratos');
export const getDocumentosEmpresa = () => api.get('/documentos-empresa');
export const uploadDocumentosEmpresa = (file, nombre) => {
  const fd = new FormData();
  fd.append('file', file);
  if (nombre) fd.append('nombre', nombre);
  return api.post('/documentos-empresa', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const renameDocumentoEmpresa = (id, nombre) => api.patch(`/documentos-empresa/${id}`, { nombre });
export const deleteDocumentoEmpresa = (id) => api.delete(`/documentos-empresa/${id}`);
export const getTendencias2 = (params = {}) => api.get('/stats/tendencias2', { params });
export const getTendencias2Pdf = (params = {}) => api.get('/stats/tendencias2/pdf', { params });

// Clientes unificado (con locales anidados)
export const getClientesCompleto = () => api.get('/clientes-completo');
export const createClienteWithLocales = (data, config) => api.post('/clientes/with-locales', data, config);
export const updateClienteWithLocales = (id, data) => api.put(`/clientes/${id}/with-locales`, data);
export const addObservacionCliente = (id, texto) => api.post(`/clientes/${id}/observaciones`, { texto });
export const deleteObservacionCliente = (id, obsId) => api.delete(`/clientes/${id}/observaciones/${obsId}`);
export const addAvisoCliente = (id, texto, urgente) => api.post(`/clientes/${id}/avisos`, { texto, urgente });
export const deleteAvisoCliente = (id, avisoId) => api.delete(`/clientes/${id}/avisos/${avisoId}`);
export const toggleActivoCliente = (id, motivo, observaciones) => api.post(`/clientes/${id}/toggle-activo`, { motivo, observaciones });
export const toggleActivoLocal = (id, motivo, observaciones) => api.post(`/locales/${id}/toggle-activo`, { motivo, observaciones });

// Mapeo de estaciones del local
export const setLocalMapeo = (id, data) => api.put(`/locales/${id}/mapeo`, data);
export const clearLocalMapeo = (id) => api.delete(`/locales/${id}/mapeo`);
export const uploadPlano = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/locales/${id}/plano`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Certificaciones
export const getCertifConfig = (clienteId) => api.get(`/clientes/${clienteId}/certif-config`);
export const setCertifConfig = (clienteId, data) => api.put(`/clientes/${clienteId}/certif-config`, data);
export const listCertificaciones = (params = {}) => api.get('/certificaciones', { params });
export const emitirCertificacion = (data) => api.post('/certificaciones', data);
export const getCertifRegistro = () => api.get('/certificaciones/registro');
export const guardarCertifRegistro = (localId, data) => api.put(`/certificaciones/registro/${localId}`, data);
export const getCertifPropuesta = (localIds, modo) => api.get('/certificaciones/propuesta', { params: { local_ids: localIds.join(','), modo } });
export const getCertificacion = (id) => api.get(`/certificaciones/${id}`);
export const eliminarCertificacion = (id) => api.delete(`/certificaciones/${id}`);
export const getCertifTextoGlobal = () => api.get('/settings/certif-texto');
export const setCertifTextoGlobal = (texto, firma_imagen = null) => api.put('/settings/certif-texto', { texto, firma_imagen });
export const enviarCorreoCertificacion = (certId, payload) => api.post(`/certificaciones/${certId}/enviar-correo`, payload);
export const getCorreoDestinatariosCertificacion = (certId) => api.get(`/certificaciones/${certId}/correo-destinatarios`);
export const enviarCorreoServicio = (servicioId, payload) => api.post(`/servicios/${servicioId}/enviar-correo`, payload);
export const getCorreoDestinatariosServicio = (servicioId) => api.get(`/servicios/${servicioId}/correo-destinatarios`);


// Productos (catálogo dinámico)
export const getProductos = (params = {}) => api.get('/productos', { params });
export const createProducto = (data) => api.post('/productos', data);
export const updateProducto = (id, data) => api.patch(`/productos/${id}`, data);
export const deleteProducto = (id) => api.delete(`/productos/${id}`);

// Planilla v2
export const getNumeroVisita = (local_id, fecha) => api.get('/numero-visita', { params: { local_id, fecha } });
export const createPlanillaV2 = (data) => api.post('/planilla-v2', data);
export const updatePlanillaV2 = (servicioId, data) => api.put(`/planilla-v2/${servicioId}`, data);
export const deleteCliente = (id) => api.delete(`/clientes/${id}`);

// Locales
export const getLocales = (params = {}) => api.get('/locales', { params });
export const getLocal = (id) => api.get(`/locales/${id}`);
export const createLocal = (data) => api.post('/locales', data);
export const updateLocal = (id, data) => api.patch(`/locales/${id}`, data);
export const deleteLocal = (id) => api.delete(`/locales/${id}`);

// Servicios
export const getServicios = (params = {}) => api.get('/servicios', { params });
export const getHistorialServicios = (params = {}) => api.get('/servicios/historial', { params });
export const getServicio = (id) => api.get(`/servicios/${id}`);
export const createServicio = (data) => api.post('/servicios', data);
export const updateServicio = (id, data) => api.patch(`/servicios/${id}`, data);

// Hallazgos
export const createHallazgo = (formData) => api.post('/hallazgos', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteHallazgo = (id) => api.delete(`/hallazgos/${id}`);

// Productos aplicados a un servicio (legacy)
export const createProductoAplicado = (servicioId, data) => api.post(`/productos?servicio_id=${servicioId}`, data);
export const deleteProductoAplicado = (id) => api.delete(`/productos/${id}`);

// Recomendaciones
export const createRecomendacion = (servicioId, data) => api.post(`/recomendaciones?servicio_id=${servicioId}`, data);
export const deleteRecomendacion = (id) => api.delete(`/recomendaciones/${id}`);

// Upload
export const uploadFoto = (file) => {
  const formData = new FormData();
  formData.append('foto', file);
  return api.post('/upload/foto', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const uploadFirma = (file) => {
  const formData = new FormData();
  formData.append('firma', file);
  return api.post('/upload/firma', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Reportes
export const createReporte = (data) => api.post('/reportes', data);
export const getReportesByServicio = (servicioId) => api.get(`/reportes/servicio/${servicioId}`);
export const deleteServicio = (id) => api.delete(`/servicios/${id}`);
export const generarPDF = (servicioId) => api.post(`/servicios/${servicioId}/generar-pdf`);
export const sendReporteEmail = (reporteId, emailTo) => api.post(`/reportes/${reporteId}/send-email`, null, {
  params: { email_to: emailTo }
});

// Dashboard Stats
export const getDashboardStats = () => api.get('/stats/dashboard');
export const getTablon = () => api.get('/tablon');
export const marcarTablonVistos = (data) => api.post('/tablon/vistos', data);
export const crearRecordatorio = (data) => api.post('/tablon/recordatorios', data);

// Tendencias y Analytics
export const getTendenciasPlagas = (params = {}) => api.get('/stats/tendencias-plagas', { params });
export const getComparativoLocales = (params = {}) => api.get('/stats/comparativo-locales', { params });
export const getConsumoProductos = (params = {}) => api.get('/stats/consumo-productos', { params });
export const getServiciosPorTipoMes = (params = {}) => api.get('/stats/servicios-por-tipo-mes', { params });

// Supervisor
export const getServiciosPendientes = (params = {}) => api.get('/aprobaciones/pendientes', { params });
export const getServiciosRechazados = (params = {}) => api.get('/aprobaciones/rechazadas', { params });
export const aprobarServicio = (id, observaciones) => api.post(`/servicios/${id}/aprobar`, { observaciones_supervisor: observaciones });
export const rechazarServicio = (id, observaciones) => api.post(`/servicios/${id}/rechazar`, { observaciones_supervisor: observaciones });

// Empresa
export const getEmpresaInfo = () => api.get('/empresa/info');

// Técnicos - Score y Ranking
export const getTecnicoScore = (id, params = {}) => api.get(`/tecnicos/${id}/score`, { params });
export const getTecnicosRanking = () => api.get('/tecnicos/ranking');

// Cumplimiento Global
export const getCumplimientoGlobal = (params = {}) => api.get('/stats/cumplimiento-global', { params });
export const getCuadroAnual = (anio, params = {}) => api.get(`/stats/cuadro-anual/${anio}`, { params });
// Justificativos de Cumplimiento
export const getJustificativos = (params = {}) => api.get('/justificativos', { params });
export const createJustificativo = (data) => api.post('/justificativos', data);
export const deleteJustificativo = (id) => api.delete(`/justificativos/${id}`);

// Tendencias avanzadas
export const getTendenciasInsectos = (params = {}) => api.get('/stats/tendencias-insectos', { params });
export const getTendenciasRoedoresEstaciones = (params = {}) => api.get('/stats/tendencias-roedores-estaciones', { params });

// Exportar Excel (binary download)
export const exportServiciosExcel = (params = {}) => api.get('/export/servicios-excel', { params, responseType: 'blob' });
export const exportLocalesExcel = () => api.get('/clientes/locales-excel', { responseType: 'blob' });
export const modeloLocalesExcel = () => api.get('/clientes/locales-excel/modelo', { responseType: 'blob' });
export const importarLocalesExcel = (archivo) => {
  const fd = new FormData();
  fd.append('archivo', archivo);
  return api.post('/clientes/importar-excel', fd, { timeout: 180000 });
};

// Auditorías Normativas
export const getPlantillasAuditoria = () => api.get('/auditorias/plantillas');
export const getPlantillaDetalle = (id) => api.get(`/auditorias/plantillas/${id}`);
export const addPlantillaItem = (id, data) => api.post(`/auditorias/plantillas/${id}/items`, data);
export const getPlantillaItemsAdmin = (id) => api.get(`/auditorias/plantillas/${id}/items`);
export const quitarPlantillaItem = (id, data) => api.post(`/auditorias/plantillas/${id}/items/quitar`, data);
export const restaurarPlantillaItem = (id, data) => api.post(`/auditorias/plantillas/${id}/items/restaurar`, data);
export const getAuditorias = (params = {}) => api.get('/auditorias', { params });
export const getAuditoria = (id) => api.get(`/auditorias/${id}`);
export const createAuditoria = (data) => api.post('/auditorias', data);
export const updateAuditoria = (id, data) => api.patch(`/auditorias/${id}`, data);
export const deleteAuditoria = (id) => api.delete(`/auditorias/${id}`);
export const generateAuditoriaPDF = (id) => api.post(`/auditorias/${id}/pdf`);

// ==================== CLIENTE PORTAL ====================
export const clienteLogin = (email, password) => api.post('/cliente-auth/login', { email, password });
export const getClienteMe = () => api.get('/cliente-portal/me');
export const getClienteLocales = () => api.get('/cliente-portal/locales');
export const getClienteServicios = (params = {}) => api.get('/cliente-portal/servicios', { params });
export const getClienteStats = () => api.get('/cliente-portal/stats');
export const getClientePendientes = () => api.get('/cliente-portal/pendientes');
export const getClientePlanos = () => api.get('/cliente-portal/planos');

// Planos interactivos
export const getTiposDispositivo = () => api.get('/tipos-dispositivo');
export const createTipoDispositivo = (data) => api.post('/tipos-dispositivo', data);
export const updateTipoDispositivo = (id, data) => api.patch(`/tipos-dispositivo/${id}`, data);
export const getPlanos = (params) => api.get('/planos', { params });
export const getPlano = (id) => api.get(`/planos/${id}`);
export const contarPaginasPdf = (formData) => api.post('/planos/pdf-paginas', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const importarPlano = (formData) => api.post('/planos', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updatePlanoMeta = (id, data) => api.patch(`/planos/${id}`, data);
export const guardarBorradorPlano = (id, data) => api.put(`/planos/${id}/borrador`, data);
export const descartarBorradorPlano = (id) => api.post(`/planos/${id}/descartar-borrador`);
export const publicarPlano = (id) => api.post(`/planos/${id}/publicar`);
export const deletePlano = (id) => api.delete(`/planos/${id}`);
export const getPlanoAuditoria = (id) => api.get(`/planos/${id}/auditoria`);
export const getPlanoPdf = (id, modo) => api.get(`/planos/${id}/pdf`, { params: { modo }, responseType: 'blob' });
export const ubicarEstacionesPlano = (id, data) => api.post(`/planos/${id}/ubicar-estaciones`, data);
// Guarda en el dispositivo (caché del service worker) los planos publicados de un local para consultarlos sin señal
export const precargarPlanosLocal = async (localId) => {
  if (!localId) return;
  try {
    const r = await getPlanos({ local_id: localId });
    await getTiposDispositivo().catch(() => {});
    const base = process.env.REACT_APP_BACKEND_URL;
    await Promise.all((r.data || []).filter((p) => p.publicada && p.imagen_url).map((p) => fetch(p.imagen_url.startsWith('/api') ? `${base}${p.imagen_url}` : p.imagen_url).catch(() => {})));
  } catch (e) { /* sin red: se usará la copia ya guardada */ }
};
export const getClienteServicioDetalle = (id) => api.get(`/cliente-portal/servicio/${id}`);
export const getClienteCalendario = (params = {}) => api.get('/cliente-portal/calendario', { params });
export const getClienteCuadroAnual = (anio, params = {}) => api.get(`/cliente-portal/cuadro-anual/${anio}`, { params });
export const getClienteTendencias2 = (params = {}) => api.get('/cliente-portal/tendencias2', { params });
export const getClienteTendencias2Pdf = (params = {}) => api.get('/cliente-portal/tendencias2/pdf', { params, responseType: 'blob', timeout: 180000 });
export const getClienteServicioFull = (id) => api.get(`/cliente-portal/servicio-full/${id}`);
export const getClienteAuditorias = (params = {}) => api.get('/cliente-portal/auditorias', { params });
export const getClienteAuditoria = (id) => api.get(`/cliente-portal/auditorias/${id}`);
export const getClienteDocumentosEmpresa = () => api.get('/cliente-portal/documentos-empresa');
export const getClienteCertificaciones = (params = {}) => api.get('/cliente-portal/certificaciones', { params });
export const getClienteCalendarioPdf = (params = {}) =>
  api.get('/cliente-portal/calendario/pdf', { params, responseType: 'blob' });

// Admin Cliente Portal Management
export const setClientePortalAcceso = (clienteId, data) => api.put(`/clientes/${clienteId}/portal-acceso`, data);
export const toggleClienteAccess = (clienteId) => api.post(`/clientes/${clienteId}/toggle-access`);

// ===== Portal por local (correo de la llave + clave del local) =====
export const setLocalPortal = (cliente_id, local_id, payload) =>
  api.put(`/clientes/${cliente_id}/locales/${local_id}/portal`, payload);

// ===== Programación de Servicios =====
export const getOrdenes = (params = {}) => api.get('/ordenes', { params });
export const getOrden = (id) => api.get(`/ordenes/${id}`);
export const createOrden = (data, config) => api.post('/ordenes', data, config);
export const createComanda = (data, config) => api.post('/comandas', data, config);
export const getComandaCompleta = (id) => api.get(`/comandas/${id}`, { params: { completo: true } });
export const updateComanda = (id, data, config) => api.put(`/comandas/${id}`, data, config);
export const agruparRuta = (ordenIds) => api.post('/comandas/agrupar', { orden_ids: ordenIds });
export const updateOrden = (id, data) => api.patch(`/ordenes/${id}`, data);
export const getReasignarOrden = (id, params = {}) => api.get(`/ordenes/${id}/reasignar`, { params });
export const reasignarOrden = (id, data) => api.post(`/ordenes/${id}/reasignar`, data);
export const cambiarEstadoOrden = (id, estado, observaciones_estado, motivo_tarde) =>
  api.patch(`/ordenes/${id}/estado`, { estado, observaciones_estado, motivo_tarde });
export const instalarEstacionMapeo = (localId, body) => api.post(`/locales/${localId}/mapeo/estacion`, body);
export const getGeocerca = () => api.get('/config/geocerca');
export const putGeocerca = (body) => api.put('/config/geocerca', body);
// Membrete de certificados (logo fijo de Freshen - Em + asociaciones)
export const getMembrete = () => api.get('/settings/membrete');
export const putMembreteTextos = (body) => api.put('/settings/membrete/textos', body);
export const putMembreteFirma = (body) => api.put('/settings/membrete/firma', body);
export const crearAsociacionMembrete = (fd) => api.post('/settings/membrete/asociaciones', fd);
export const editarAsociacionMembrete = (id, body) => api.patch(`/settings/membrete/asociaciones/${id}`, body);
export const ordenarAsociacionesMembrete = (ids) => api.put('/settings/membrete/asociaciones-orden', { ids });
export const borrarAsociacionMembrete = (id) => api.delete(`/settings/membrete/asociaciones/${id}`);
export const vistaPreviaMembrete = () => api.post('/settings/membrete/vista-previa', null, { responseType: 'blob', timeout: 60000 });
export const deleteOrden = (id) => api.delete(`/ordenes/${id}`);
export const getStatsTecnicoOrdenes = (params = {}) => api.get('/ordenes/stats/tecnico', { params });
export const getCalendarioOrdenesPdf = (params) =>
  api.get('/ordenes/calendario/pdf', { params, responseType: 'blob' });
export const getEnvioCalendario = (params) => api.get('/ordenes/calendario/envio', { params });
export const enviarCalendario = (data) => api.post('/ordenes/calendario/enviar', data, { timeout: 180000 });

// ===== Portal del Técnico — Transiciones con GPS =====
export const ordenDirigirme = (id) => api.post(`/ordenes/${id}/dirigirme`);
export const ordenLlegue = (id, lat, lng, motivo_tarde) => api.post(`/ordenes/${id}/llegue`, { lat, lng, motivo_tarde });
export const evaluarLlegadaTarde = (id, afecta) => api.patch(`/ordenes/${id}/llegada-tarde`, { afecta });
export const ordenIniciarPlanilla = (id) => api.post(`/ordenes/${id}/iniciar-planilla`);
export const delegarPuntero = (id, tecnico_id) => api.post(`/ordenes/${id}/delegar-puntero`, { tecnico_id });
export const ordenCambiarTipo = (id, tipo_nuevo, observacion) =>
  api.post(`/ordenes/${id}/cambiar-tipo`, { tipo_nuevo, observacion });

// ===== Edición colaborativa de planilla por OT =====
// Con `version` el servidor sólo devuelve el borrador si alguien lo cambió (si no: { sin_cambios: true })
export const getPlanillaDraft = (orden_id, version) =>
  api.get(`/ordenes/${orden_id}/planilla-draft`, version == null ? undefined : { params: { version } });
export const patchPlanillaDraft = (orden_id, draft) =>
  api.patch(`/ordenes/${orden_id}/planilla-draft`, { draft });
export const patchPlanillaDraftOps = (orden_id, ops, base_version) =>
  api.patch(`/ordenes/${orden_id}/planilla-draft/ops`, { ops, base_version });

// ===== Asistencia =====
export const getEmpresaGeo = () => api.get('/asistencia/empresa');
export const getAsistenciaHoy = () => api.get('/asistencia/hoy');
// Rastreo GPS del personal en jornada
export const enviarPosicionRastreo = (body) => api.post('/rastreo/posicion', body, { timeout: 10000 });
export const reportarEstadoGps = (apagado) => api.post('/rastreo/estado-gps', { apagado }, { timeout: 10000 });
export const getRastreoEnVivo = () => api.get('/rastreo/en-vivo');
export const getRecorridoTecnico = (tecnicoId) => api.get(`/rastreo/recorrido/${tecnicoId}`);
export const marcarEntradaAsistencia = (payload) => api.post('/asistencia/marcar-entrada', payload);
export const marcarSalidaAsistencia = (payload) => api.post('/asistencia/marcar-salida', payload);
export const getAsistenciaHistorial = (params = {}) => api.get('/asistencia/historial', { params });
export const actualizarTareasOrden = (orden_id, tareas) => api.patch(`/ordenes/${orden_id}/tareas`, { tareas });
// Portal del técnico: avisos de servicios revisados e historial de aprobaciones (últimos 7 días)
export const getMisAvisos = () => api.get('/tecnico/avisos');
export const marcarAvisosVistos = (ids) => api.post('/tecnico/avisos/vistos', { ids });
export const getMisAprobaciones = () => api.get('/tecnico/aprobaciones');
// Configuración → Backup (sólo admin)
export const crearBackup = () => api.post('/backup');
export const listarBackups = () => api.get('/backup');
export const enlaceBackup = (id) => api.post(`/backup/${id}/enlace`);
export const borrarBackup = (id) => api.delete(`/backup/${id}`);
export const copiaMensual = () => api.get('/backup/mensual');
export const restaurarDesdeMensual = () => api.post('/backup/mensual/restaurar', null, { timeout: 0 });
export const iniciarRestauracion = (nombre, tamano) => api.post('/backup/restaurar/inicio', { nombre, tamano });
export const subirParteRestauracion = (uid, offset, blob) =>
  api.put(`/backup/restaurar/${uid}/parte`, blob, { params: { offset }, headers: { 'Content-Type': 'application/octet-stream' } });
export const analizarRestauracion = (uid) => api.post(`/backup/restaurar/${uid}/analizar`);
export const aplicarRestauracion = (uid, seleccion) => api.post(`/backup/restaurar/${uid}/aplicar`, seleccion ? { seleccion } : null, { timeout: 0 });
export const estadoOptimizacionFotos = () => api.get('/backup/fotos');
export const fotosPendientes = () => api.get('/backup/fotos/pendientes', { timeout: 0 });
export const optimizarFotos = () => api.post('/backup/fotos/optimizar');

// Pendientes de locales
export const getPendientes = (params = {}) => api.get('/pendientes', { params });
export const createPendiente = (data) => api.post('/pendientes', data);
export const derivarComentarioInterno = (servicioId, nombre, ordenId) => api.post(`/servicios/${servicioId}/comentario-interno/derivar`, { nombre, orden_id: ordenId || null });
export const updatePendiente = (id, data) => api.patch(`/pendientes/${id}`, data);
export const deletePendiente = (id) => api.delete(`/pendientes/${id}`);
export const getOpcionesOrdenPendiente = (local_id, mes) => api.get('/pendientes/opciones-orden', { params: { local_id, mes } });
export const asignarPendienteOrden = (id, orden_id) => api.post(`/pendientes/${id}/asignar-orden`, { orden_id });
export const quitarPendienteOrden = (id) => api.post(`/pendientes/${id}/quitar-orden`);

// Próximos a vencer
export const getVencimientos = () => api.get('/vencimientos');
export const createVencimiento = (data) => api.post('/vencimientos', data);
export const deleteVencimiento = (id) => api.delete(`/vencimientos/${id}`);

// Rendimiento
export const getAsuntosRendimiento = () => api.get('/rendimiento/asuntos');
export const getRegistrosRendimiento = (tipo, params = {}) => api.get(`/rendimiento/${tipo}`, { params });
export const createRegistroRendimiento = (tipo, formData, config = {}) =>
  api.post(`/rendimiento/${tipo}`, formData, { ...config, headers: { 'Content-Type': 'multipart/form-data', ...(config.headers || {}) } });
export const deleteRegistroRendimiento = (tipo, id) => api.delete(`/rendimiento/${tipo}/${id}`);
export const getConclusionesRendimiento = (params = {}) => api.get('/rendimiento/conclusiones', { params });
export const getConclusionesHistorico = (params = {}) => api.get('/rendimiento/conclusiones/historico', { params });
export const getConclusionesPdf = (params = {}) => api.get('/rendimiento/conclusiones/pdf', { params });
export const getMapaClientes = () => api.get('/mapa-clientes');
export const getOficinaCentral = () => api.get('/rutas/oficina');
export const setOficinaCentral = (data) => api.put('/rutas/oficina', data);
export const resolverUbicacion = (texto) => api.post('/rutas/resolver-ubicacion', { texto });
export const optimizarRuta = (data) => api.post('/rutas/optimizar', data);
export const getRutasProgramadas = (fecha) => api.get('/rutas/programadas', { params: { fecha } });
export const buscarRutasCercanas = (data) => api.post('/rutas/cercanas', data);
export const getGIUsuarios = () => api.get('/gestion-interna/usuarios');
export const getGIContador = () => api.get('/gestion-interna/contador');
export const getGITareas = (params) => api.get('/gestion-interna/tareas', { params });
export const getGITarea = (id) => api.get(`/gestion-interna/tareas/${id}`);
export const crearGITarea = (data) => api.post('/gestion-interna/tareas', data);
export const editarGITarea = (id, data) => api.patch(`/gestion-interna/tareas/${id}`, data);
export const moverGITarea = (id, data) => api.post(`/gestion-interna/tareas/${id}/movimientos`, data);
export const eliminarGITarea = (id) => api.delete(`/gestion-interna/tareas/${id}`);
export const getGIOpcionesOrden = (localId, mes) => api.get('/gestion-interna/opciones-orden', { params: { local_id: localId, mes } });
export const agregarPendienteGI = (id, data) => api.post(`/gestion-interna/tareas/${id}/pendiente`, data);
export const iniciarSubidaGI = (nombre, tamano) => api.post('/gestion-interna/adjuntos/iniciar', { nombre, tamano });
export const subirParteGI = (uploadId, offset, blob) => {
  const fd = new FormData();
  fd.append('offset', String(offset));
  fd.append('parte', blob, 'parte');
  return api.post(`/gestion-interna/adjuntos/${uploadId}/parte`, fd, { timeout: 120000 });
};
export const completarSubidaGI = (uploadId) => api.post(`/gestion-interna/adjuntos/${uploadId}/completar`, null, { timeout: 180000 });
export const setEstadoMarcacion = (data) => api.patch('/asistencia/estado-marcacion', data);
export const eliminarMarcacion = (data) => api.post('/asistencia/marcacion/eliminar', data);

// Auditoría de Servicio
export const getPlantillasAuditoriaServicio = () => api.get('/auditorias-servicio/plantillas');
export const setPonderacionesAuditoriaServicio = (pesos) => api.put('/auditorias-servicio/ponderaciones', { pesos });
export const itemsServicioApi = {
  listar: (tipo) => api.get(`/auditorias-servicio/plantillas/${tipo}/items`),
  agregar: (tipo, data) => api.post(`/auditorias-servicio/plantillas/${tipo}/items`, data),
  quitar: (tipo, data) => api.post(`/auditorias-servicio/plantillas/${tipo}/items/quitar`, data),
  restaurar: (tipo, data) => api.post(`/auditorias-servicio/plantillas/${tipo}/items/restaurar`, data),
};
export const getServiciosDisponiblesAuditoria = (params) => api.get('/auditorias-servicio/servicios-disponibles', { params });
export const createAuditoriaServicio = (data) => api.post('/auditorias-servicio', data);
export const getAuditoriasServicio = (params = {}) => api.get('/auditorias-servicio', { params });
export const getAuditoriaServicio = (id) => api.get(`/auditorias-servicio/${id}`);
export const updateAuditoriaServicio = (id, data) => api.patch(`/auditorias-servicio/${id}`, data);
export const deleteAuditoriaServicio = (id) => api.delete(`/auditorias-servicio/${id}`);
export const getAuditoriaServicioPdf = (id) => api.get(`/auditorias-servicio/${id}/pdf`, { responseType: 'blob' });

// Unidades (móviles)
export const getUnidades = () => api.get('/unidades');
export const getTiposEstablecimiento = () => api.get('/settings/tipos-establecimiento');
export const setTiposEstablecimiento = (items) => api.put('/settings/tipos-establecimiento', { items });

// Stock
export const getStockArticulos = () => api.get('/stock/articulos');
export const createStockArticulo = (data) => api.post('/stock/articulos', data);
export const updateStockArticulo = (id, data) => api.put(`/stock/articulos/${id}`, data);
export const deleteStockArticulo = (id) => api.delete(`/stock/articulos/${id}`);
export const getStockMovimientos = (params = {}) => api.get('/stock/movimientos', { params });
export const getStockTendencias = (params = {}) => api.get('/stock/tendencias', { params });
export const getStockTendenciasPdf = (params = {}) => api.get('/stock/tendencias/pdf', { params, responseType: 'blob' });
export const recuperarPasswordPortal = (email) => api.post('/cliente-portal/recuperar-password', { email });
export const restablecerPasswordPortal = (data) => api.post('/cliente-portal/restablecer-password', data);
export const createStockMovimiento = (data, config) => api.post('/stock/movimientos', data, config);
export const createStockSalidaLote = (data, config) => api.post('/stock/salidas', data, config);
export const deleteStockMovimiento = (id) => api.delete(`/stock/movimientos/${id}`);
export const createUnidad = (data) => api.post('/unidades', data);
export const updateUnidad = (id, data) => api.put(`/unidades/${id}`, data);
export const deleteUnidad = (id) => api.delete(`/unidades/${id}`);

// Papelera de Reciclaje
export const getPapelera = (params = {}) => api.get('/papelera', { params });
export const restaurarPapelera = (id) => api.post(`/papelera/${id}/restaurar`);
export const eliminarPapeleraDefinitivo = (id) => api.delete(`/papelera/${id}`);

export default api;
// ==================== HISTORIAL DE CORREOS ====================
export const getCorreos = (params = {}) => api.get('/correos', { params });
export const reenviarCorreo = (logId) => api.post(`/correos/${logId}/reenviar`);
